import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import type { Dispatch } from "../types";

export type DispatchValidationMode = "approve" | "reject";

/** Payload emitted by the dialog once the user confirms. */
export type DispatchValidationSubmit =
  | { mode: "approve"; invoice_number: string; observation?: string }
  | { mode: "reject"; observation: string };

export interface DispatchValidationDialogProps {
  open: boolean;
  mode: DispatchValidationMode;
  dispatch: Dispatch | null;
  onClose: () => void;
  onConfirm: (result: DispatchValidationSubmit) => Promise<void>;
}

/**
 * Approve/reject dialog for a dispatch. The approve mode requires the invoice
 * number; the reject mode requires an observation. `onConfirm` may reject: the
 * dialog stays open so the page handler can surface the error, and closes on
 * success.
 */
export function DispatchValidationDialog({
  open,
  mode,
  dispatch,
  onClose,
  onConfirm,
}: DispatchValidationDialogProps) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [observation, setObservation] = useState("");
  const [invoiceTouched, setInvoiceTouched] = useState(false);
  const [observationTouched, setObservationTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setInvoiceNumber("");
      setObservation("");
      setInvoiceTouched(false);
      setObservationTouched(false);
      setIsSubmitting(false);
    }
  }, [open, mode, dispatch?.id]);

  const isApprove = mode === "approve";
  const invoiceEmpty = invoiceNumber.trim().length === 0;
  const observationEmpty = observation.trim().length === 0;
  const submitDisabled =
    isSubmitting || (isApprove ? invoiceEmpty : observationEmpty);

  const quote =
    dispatch?.quote_number || dispatch?.reservation?.quote_number || null;

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setInvoiceTouched(true);
    setObservationTouched(true);
    setIsSubmitting(true);
    try {
      if (isApprove) {
        await onConfirm({
          mode: "approve",
          invoice_number: invoiceNumber.trim(),
          observation: observation.trim() || undefined,
        });
      } else {
        await onConfirm({ mode: "reject", observation: observation.trim() });
      }
      onClose();
    } catch {
      // The page already shows an error toast; keep the dialog open to retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isApprove ? "Aprobar despacho" : "Rechazar despacho"}
        {dispatch ? ` #${dispatch.id}` : ""}
        {quote ? ` — ${quote}` : ""}
      </DialogTitle>

      <DialogContent>
        {isApprove && (
          <TextField
            autoFocus
            label="Factura"
            variant="outlined"
            fullWidth
            margin="normal"
            value={invoiceNumber}
            onChange={(e) => {
              setInvoiceNumber(e.target.value);
              setInvoiceTouched(true);
            }}
            onBlur={() => setInvoiceTouched(true)}
            inputProps={{ maxLength: 100, "aria-label": "invoice-number" }}
            error={invoiceTouched && invoiceEmpty}
            helperText={
              invoiceTouched && invoiceEmpty
                ? "La factura es obligatoria"
                : undefined
            }
          />
        )}

        <TextField
          label="Observaciones"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          margin="normal"
          value={observation}
          onChange={(e) => {
            setObservation(e.target.value);
            setObservationTouched(true);
          }}
          onBlur={() => setObservationTouched(true)}
          inputProps={{ maxLength: 500, "aria-label": "dispatch-observation" }}
          error={!isApprove && observationTouched && observationEmpty}
          helperText={
            !isApprove && observationTouched && observationEmpty
              ? "La observación es obligatoria"
              : undefined
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color={isApprove ? "primary" : "secondary"}
          variant="contained"
          disabled={submitDisabled}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} />
            ) : isApprove ? (
              <CheckCircleIcon />
            ) : (
              <CancelIcon />
            )
          }
        >
          {isApprove ? "Aprobar" : "Rechazar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DispatchValidationDialog;
